import React from 'react';
import { Helmet } from 'react-helmet-async';
interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
}
export function SEO({
  title = 'Justus Kraft - CRM Expert | Sales Automation Specialist',
  description = 'Started my first business at 16. Now at 23, I help companies double their sales through intelligent CRM systems. 110+ happy clients, GDPR compliant, $7M+ in additional revenue generated.',
  keywords = 'CRM expert, sales automation, CRM consulting, Justus Kraft, CRM Kraft, B2B sales, B2C sales, CRM systems, sales optimization, revenue growth, lead management, customer relationship management',
  ogImage = 'https://cdn.magicpatterns.com/uploads/jWaCxaLsc32b7Nk2LkzDn3/62dfd10966fa026013ebcbcf_565aa1ce-min-p-1600_(3).jpg',
  ogType = 'website',
  canonical
}: SEOProps) {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://crm-kraft.com';
  const currentUrl = typeof window !== 'undefined' ? window.location.href : siteUrl;
  const canonicalUrl = canonical || currentUrl;
  const faviconUrl = 'https://cdn.magicpatterns.com/uploads/vy5bw8FhBjPCpPJJJ1fQji/facicon-jk-144.png';
  return <Helmet data-id="element-222">
      {/* Primary Meta Tags */}
      <title data-id="element-223">{title}</title>
      <meta name="title" content={title} data-id="element-224" />
      <meta name="description" content={description} data-id="element-225" />
      <meta name="keywords" content={keywords} data-id="element-226" />

      {/* Favicon - Multiple sizes for different contexts */}
      <link rel="icon" type="image/png" sizes="144x144" href={faviconUrl} data-id="element-227" />
      <link rel="icon" type="image/png" sizes="32x32" href={faviconUrl} data-id="element-228" />
      <link rel="icon" type="image/png" sizes="16x16" href={faviconUrl} data-id="element-229" />
      <link rel="shortcut icon" href={faviconUrl} data-id="element-230" />

      {/* Apple Touch Icon */}
      <link rel="apple-touch-icon" sizes="144x144" href={faviconUrl} data-id="element-231" />

      {/* Android/Chrome */}
      <link rel="icon" sizes="192x192" href={faviconUrl} data-id="element-232" />

      {/* Microsoft Tiles */}
      <meta name="msapplication-TileImage" content={faviconUrl} data-id="element-233" />
      <meta name="msapplication-TileColor" content="#0A0E27" data-id="element-234" />

      {/* Language */}
      <html lang="en" data-id="element-235" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} data-id="element-236" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} data-id="element-237" />
      <meta property="og:url" content={currentUrl} data-id="element-238" />
      <meta property="og:title" content={title} data-id="element-239" />
      <meta property="og:description" content={description} data-id="element-240" />
      <meta property="og:image" content={ogImage} data-id="element-241" />
      <meta property="og:locale" content="en_US" data-id="element-242" />
      <meta property="og:site_name" content="CRM Kraft" data-id="element-243" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" data-id="element-244" />
      <meta property="twitter:url" content={currentUrl} data-id="element-245" />
      <meta property="twitter:title" content={title} data-id="element-246" />
      <meta property="twitter:description" content={description} data-id="element-247" />
      <meta property="twitter:image" content={ogImage} data-id="element-248" />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" data-id="element-249" />
      <meta name="author" content="Justus Kraft" data-id="element-250" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" data-id="element-251" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" data-id="element-252" />

      {/* Theme Color for mobile browsers */}
      <meta name="theme-color" content="#0A0E27" data-id="element-253" />

      {/* Structured Data - Person/Organization */}
      <script type="application/ld+json" data-id="element-254">
        {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: 'Justus Kraft',
        jobTitle: 'CRM Expert & Sales Automation Specialist',
        description: 'CRM consultant and sales automation expert with IT and sales background. Helping companies double their revenue through intelligent CRM systems.',
        url: siteUrl,
        image: ogImage,
        sameAs: [
          // Add social media profiles here when available
        ],
        knowsAbout: ['CRM Systems', 'Sales Automation', 'B2B Sales', 'B2C Sales', 'IT Development', 'Lead Management', 'Revenue Optimization'],
        alumniOf: {
          '@type': 'EducationalOrganization',
          name: 'Self-employed entrepreneur since age 16'
        }
      })}
      </script>

      {/* Structured Data - Service */}
      <script type="application/ld+json" data-id="element-255">
        {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'CRM Consulting and Sales Automation',
        provider: {
          '@type': 'Person',
          name: 'Justus Kraft'
        },
        areaServed: [{
          '@type': 'Country',
          name: 'Germany'
        }, {
          '@type': 'Country',
          name: 'Europe'
        }],
        description: 'Professional CRM consulting and sales automation for businesses. Combining IT expertise with sales experience to deliver practical, high-adoption CRM solutions.',
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          price: '0',
          priceCurrency: 'EUR',
          description: 'Free initial consultation'
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5',
          reviewCount: '110'
        }
      })}
      </script>

      {/* Structured Data - Organization */}
      <script type="application/ld+json" data-id="element-256">
        {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'CRM Kraft',
        founder: {
          '@type': 'Person',
          name: 'Justus Kraft'
        },
        description: 'CRM systems and sales automation solutions built by practitioners for practitioners.',
        url: siteUrl,
        logo: ogImage
      })}
      </script>
    </Helmet>;
}